import { isDespiaNative } from "./isDespiaNative";

/**
 * Decide qué errores merecen viajar al backend.
 *
 * Contexto (auditoría del 2026-08-04): de las 12.642 filas históricas de
 * `error_logs`, 12.458 (98,6%) venían del preview de Lovable o de `localhost`
 * — artefactos de HMR mientras se edita código, no fallos de nadie. Cada una
 * disparaba un correo. Los errores reales de cliente quedaban sepultados.
 *
 * Esta política es la primera de dos puertas. La segunda, y la que manda, está
 * en `supabase/functions/_shared/errorPolicy.ts`: un cliente con un bundle
 * cacheado y antiguo puede saltarse ésta, así que el servidor vuelve a decidir
 * por su cuenta. Las dos listas deben mantenerse en paralelo; hay un test que
 * falla si divergen.
 */

export type ErrorEnvironment = "production" | "preview" | "development" | "unknown";

/**
 * Dominios que sirven la app real a clientes. `pesticidasesulture.co` es el
 * dominio antiguo: la migración a `ulturepest.co` es del 2026-08-03 y el viejo
 * sigue resolviendo, así que ambos cuentan como producción.
 *
 * El wrapper nativo (Despia) carga sobre estos mismos orígenes — las 81 filas
 * de móvil del histórico están todas bajo `pesticidasesulture.co` — pero se
 * comprueba `isDespiaNative()` de todos modos por si una versión futura del
 * contenedor sirve desde un origen propio.
 */
export const PRODUCTION_HOSTS = [
  "ulturepest.co",
  "www.ulturepest.co",
  "pesticidasesulture.co",
  "www.pesticidasesulture.co",
] as const;

const PREVIEW_HOST_PATTERNS = [/\.lovable\.app$/i, /\.lovableproject\.com$/i];

const DEV_HOST_PATTERNS = [
  /^localhost$/i,
  /^127\.\d+\.\d+\.\d+$/,
  /^0\.0\.0\.0$/,
  /^\[?::1\]?$/,
  /^192\.168\.\d+\.\d+$/,
  /^10\.\d+\.\d+\.\d+$/,
  /^172\.(1[6-9]|2\d|3[01])\.\d+\.\d+$/,
  /\.local$/i,
];

/** Clasifica un hostname. Exportada aparte para que el servidor pueda reusar la misma tabla. */
export function classifyHost(hostname: string): ErrorEnvironment {
  const host = hostname.trim().toLowerCase().replace(/:\d+$/, "");
  if (!host) return "unknown";
  if ((PRODUCTION_HOSTS as readonly string[]).includes(host)) return "production";
  if (PREVIEW_HOST_PATTERNS.some((re) => re.test(host))) return "preview";
  if (DEV_HOST_PATTERNS.some((re) => re.test(host))) return "development";
  return "unknown";
}

/** Entorno en el que corre esta pestaña. Todo lo que no sea producción se descarta. */
export function getEnvironment(): ErrorEnvironment {
  if (typeof window === "undefined" || !window.location) return "unknown";

  const env = classifyHost(window.location.hostname);

  // El contenedor nativo se considera producción aunque algún día sirva desde
  // un origen que no reconozcamos: allí no hay HMR ni previews.
  if (env === "unknown" && isDespiaNative()) return "production";

  return env;
}

/**
 * Mensajes que nunca son accionables, ni siquiera en producción.
 *
 * Ojo con lo que NO está aquí: `X is not defined` y `Cannot access 'x' before
 * initialization`. En un build de producción un import circular sí puede
 * producirlos de verdad, y la puerta de entorno ya elimina el 100% de los casos
 * observados (todos venían de preview o localhost). Filtrarlos además por
 * mensaje solo añadiría el riesgo de silenciar un despliegue realmente roto.
 */
/**
 * Chunk caducado tras un despliegue: el navegador tiene un `index.html` viejo
 * pidiendo assets con hashes que ya no existen. La app se recupera sola
 * recargando una vez, así que ni se avisa ni se reporta.
 */
const STALE_CHUNK_PATTERNS: RegExp[] = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i, // Safari
  /error loading dynamically imported module/i, // Firefox
  /ChunkLoadError/i,
  /Loading chunk \S+ failed/i,
  /Loading CSS chunk/i,
  /Unable to preload CSS/i,
];

/** True si el error es un chunk caducado. Lo usa el ErrorBoundary para autorecargar. */
export function isStaleChunkError(message: string): boolean {
  return STALE_CHUNK_PATTERNS.some((re) => re.test(message || ""));
}

/**
 * EL GUARDIA CONTRA EL BUCLE DE RECARGAS.
 *
 * Recargar arregla un chunk caducado, pero solo si al volver el servidor ya
 * sirve el `index.html` nuevo. Mientras un despliegue se propaga —o si una caché
 * intermedia se queda con el viejo— la página vuelve a fallar igual, recarga
 * otra vez, y ahí hay una pestaña dando vueltas: en pantalla no se ve un error,
 * se ve el fondo de la página apareciendo y desapareciendo.
 *
 * Es un ENFRIAMIENTO y no un «una sola vez» a secas: una sesión de trabajo larga
 * puede cruzarse con dos despliegues de verdad, con horas de por medio, y esa
 * segunda recarga es legítima. Lo que no puede pasar es que la segunda llegue a
 * los dos segundos de la primera.
 *
 * Va en `sessionStorage` a propósito: muere con la pestaña, que es exactamente
 * la vida del problema que resuelve.
 */
const RELOAD_MARK_KEY = "chunk_reload_at";
const RELOAD_COOLDOWN_MS = 10_000;

export function mayReloadForStaleChunk(now: number = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_MARK_KEY) ?? 0);
    return !Number.isFinite(last) || last <= 0 || now - last >= RELOAD_COOLDOWN_MS;
  } catch {
    /* Sin `sessionStorage` (modo privado antiguo, contexto restringido) se
       permite: una recarga de más es mejor que dejar la pantalla rota. */
    return true;
  }
}

export function markStaleChunkReload(now: number = Date.now()): void {
  try {
    sessionStorage.setItem(RELOAD_MARK_KEY, String(now));
  } catch {
    /* Si no se puede marcar, el enfriamiento no existe; ver arriba. */
  }
}

const NOISE_MESSAGE_PATTERNS: RegExp[] = [
  ...STALE_CHUNK_PATTERNS,

  // Bucle de ResizeObserver: lo dispara Recharts al redimensionar. Inocuo.
  /ResizeObserver/i,

  // Error cross-origin sin stack ni mensaje: cero información accionable.
  /^Script error\.?$/i,
];

/** Fallos de red que solo son ruido cuando el dispositivo está sin conexión o el usuario canceló. */
const TRANSIENT_NETWORK_PATTERNS: RegExp[] = [
  /Failed to fetch/i,
  /NetworkError when attempting to fetch/i,
  /^Load failed$/i, // Safari
  /The Internet connection appears to be offline/i,
];

const ABORT_PATTERNS: RegExp[] = [
  /AbortError/i,
  /The operation was aborted/i,
  /signal is aborted without reason/i,
  /The user aborted a request/i,
];

/** Marcos de pila que pertenecen a una extensión del navegador, no a nuestra app. */
const EXTENSION_STACK_PATTERNS: RegExp[] = [
  /chrome-extension:\/\//i,
  /moz-extension:\/\//i,
  /safari-web-extension:\/\//i,
  /safari-extension:\/\//i,
];

function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

/**
 * True si el error es ruido conocido. `stack` es opcional: sin él simplemente
 * no se puede descartar por extensión del navegador.
 */
export function isNoiseError(message: string, stack?: string): boolean {
  const msg = message || "";

  if (NOISE_MESSAGE_PATTERNS.some((re) => re.test(msg))) return true;

  // Una petición cancelada a propósito no es un fallo.
  if (ABORT_PATTERNS.some((re) => re.test(msg))) return true;

  // Un fetch fallido sin conexión es el mundo, no un bug. Con conexión sí
  // interesa: puede ser una edge function caída.
  if (isOffline() && TRANSIENT_NETWORK_PATTERNS.some((re) => re.test(msg))) return true;

  if (stack && EXTENSION_STACK_PATTERNS.some((re) => re.test(stack))) return true;

  return false;
}

export interface ReportDecision {
  report: boolean;
  /** Motivo de descarte, para el `console.warn` de diagnóstico. */
  reason?: "non_production" | "noise";
  environment: ErrorEnvironment;
}

/** Puerta única: la llama `logError()` antes de tocar la red. */
export function shouldReport(message: string, stack?: string): ReportDecision {
  const environment = getEnvironment();

  if (environment !== "production") {
    return { report: false, reason: "non_production", environment };
  }

  if (isNoiseError(message, stack)) {
    return { report: false, reason: "noise", environment };
  }

  return { report: true, environment };
}
